import api from '../services/api';
import { getFormattedValue, parsePhpArray } from '../utils/phpSerializer';

const REQUISITIONS_URL = '/api/requisitions';

export const getRequisitions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('limit', params.pageSize);
    if (params.filter) queryParams.append('search', params.filter);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.pendingApproval) queryParams.append('pendingApproval', 'true');
    if (params.pendingPMApproval) queryParams.append('pendingPMApproval', 'true');

    const url = queryParams.toString() ? `${REQUISITIONS_URL}?${queryParams}` : REQUISITIONS_URL;
    
    // Prepare headers with user context for masquerading support
    const headers = {};
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    if (authContext.user && authContext.isMasquerading) {
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    // Make API call with user context in headers
    const response = await api.get(url, { headers });
    
    // Handle ApiRequisitions format: { status: 1, message: "Success", data: { rows: [...] } }
    if (response.data && response.data.status !== undefined) {
      const requisitionsData = response.data.data?.rows || [];
      
      // IMPORTANT: Direct data manipulation for debugging
      // Based on the PHP view in requisitions.php, we need to make sure our data 
      // structure matches what the PHP view is expecting
      
      // Process data to handle PHP serialized values and mimic PHP controller logic
      const processedData = requisitionsData.map((req, index) => {
        const newReq = { ...req };
        
        // Handle missing user JOIN data
        
        // For User display: PHP view uses user_full_name directly but API lacks JOIN or has empty user data
        if (!newReq.user_full_name || newReq.user_full_name === 'Unknown' || newReq.user_full_name === '') {
          // Check if we have requisition_user that's an object with user info
          if (typeof newReq.requisition_user === 'object' && newReq.requisition_user?.user_full_name) {
            newReq.user_full_name = newReq.requisition_user.user_full_name;
          }
        else {
            // Handle the case where requisition_user is 0, empty, or null (common in this system)
            
            // Try to find user info from other fields first
            const userFields = ['requester_name', 'user_name', 'author_name', 'user_first_name', 'user_last_name'];
            let foundUserName = false;
            
            for (const field of userFields) {
              if (newReq[field] && newReq[field] !== 'Unknown' && newReq[field] !== '') {
                if (field === 'user_first_name' && newReq.user_last_name && newReq.user_last_name !== '') {
                  newReq.user_full_name = `${newReq.user_first_name} ${newReq.user_last_name}`;
                } else if (field === 'user_last_name' && newReq.user_first_name && newReq.user_first_name !== '') {
                  newReq.user_full_name = `${newReq.user_first_name} ${newReq.user_last_name}`;
                } else {
                  newReq.user_full_name = newReq[field];
                }
                foundUserName = true;
                break;
              }
            }
            
            if (!foundUserName) {
              // Last resort: Use a generic name based on the data source
              if (newReq.specification_user && newReq.specification_user !== 0 && newReq.specification_user !== '0') {
                newReq.user_full_name = `Spec User #${newReq.specification_user}`;
              } else {
                newReq.user_full_name = 'System User';
              }
            }
          }
        }
        
        // Handle missing supplier/quotation JOIN data
 
        
        // Handle any other serialized PHP data in the record
        
        // Look for company name fields that might be related to a supplier
        for (const key of Object.keys(newReq)) {
          if (key.includes('company') || key.includes('supplier')) {
            const value = newReq[key];
            if (value && typeof value === 'string' && !value.startsWith('a:')) {
              // Store this as the actual company name to use
              if (!newReq.company_name_actual) {
                newReq.company_name_actual = value;
                
                // If quotation_company_name is serialized PHP, replace it with this value
                if (typeof newReq.quotation_company_name === 'string' && 
                    newReq.quotation_company_name.startsWith('a:')) {
                  newReq.quotation_company_name = value;
                }
              }
            }
          }
        }
        
        // Also check if we have a quotation field with suppliers
        if (newReq.quotation_suppliers && Array.isArray(newReq.quotation_suppliers)) {
          if (newReq.quotation_suppliers.length > 0) {
            const selectedSupplier = newReq.quotation_suppliers.find(s => s.selected === true || s.selected === 1);
            if (selectedSupplier && selectedSupplier.company_name) {
              newReq.company_name_actual = selectedSupplier.company_name;
              newReq.quotation_company_name = selectedSupplier.company_name;
            }
          }
        }
        
        // Process PHP serialized data fields
        Object.keys(newReq).forEach(key => {
          // Check if the value is likely a PHP serialized array
          if (typeof newReq[key] === 'string' && newReq[key].startsWith('a:')) {
            try {
              let formattedValue = '';
              
              // Special handling for different field types
              if (key === 'quotation_company_name') {
                // Use company_name_actual if we found it, otherwise try to parse the serialized data
                formattedValue = newReq.company_name_actual || 
                                getFormattedValue(newReq[key], 'supplier');
              } else if (key.includes('user')) {
                formattedValue = getFormattedValue(newReq[key], 'user');
              } else {
                formattedValue = getFormattedValue(newReq[key]);
              }
              
              if (formattedValue) {
                newReq[`${key}_formatted`] = formattedValue;
                
                // Also replace the original field if appropriate
                if (key === 'user_full_name' || key === 'quotation_company_name') {
                  newReq[key] = formattedValue;
                }
              }
            } catch (e) {
              console.error(`Error parsing serialized PHP data for ${key}:`, e);
            }
          }
        });
        
        // Resolve user_full_name 
        if (newReq.user_id) {
          // Try to use user_full_name if it exists in the response
          if (!newReq.user_full_name) {
            // Fall back to using the requester_name or construct from other fields
            if (newReq.requisition_user && typeof newReq.requisition_user === 'object' && newReq.requisition_user.user_full_name) {
              newReq.user_full_name = newReq.requisition_user.user_full_name;
            }
          }
        }
        
        // DIRECT DATA FIX: If we still don't have a user_full_name, look at all properties
        // and try to find anything that might be a username
        if (!newReq.user_full_name || newReq.user_full_name === 'Unknown') {
          const potentialUserFields = Object.keys(newReq).filter(key => 
            (key.includes('user') || key.includes('name') || key.includes('requester')) && 
            typeof newReq[key] === 'string' && 
            newReq[key] !== 'Unknown'
          );
          
          // Try to assign from any potential field found
          for (const field of potentialUserFields) {
            if (newReq[field] && typeof newReq[field] === 'string' && newReq[field].length > 2) {
              newReq.user_full_name = newReq[field];
              break;
            }
          }
        }
        
        // Handle supplier/company name
        // Look at all properties to find anything that might be a company name
        if (!newReq.quotation_company_name || newReq.quotation_company_name === 'Not Selected') {
          const potentialCompanyFields = Object.keys(newReq).filter(key => 
            (key.includes('company') || key.includes('supplier') || key.includes('quotation')) && 
            typeof newReq[key] === 'string' && 
            newReq[key] !== 'Not Selected'
          );
          
          // Try to assign from any potential field found
          for (const field of potentialCompanyFields) {
            if (newReq[field] && typeof newReq[field] === 'string' && newReq[field].length > 2) {
              newReq.quotation_company_name = newReq[field];
              break;
            }
          }
        }
        
        return newReq;
      });
      
      // 🚨 CRITICAL FIX: Handle totalItems for pagination
      // The API might not provide proper total count due to missing COUNT query
      let totalItems = response.data.data?.total || response.data.data?.count || processedData.length;
      
      // If we're on page 1 and got a full page, estimate total based on page size
      const currentPage = parseInt(params.page) || 1;
      const itemsPerPage = parseInt(params.pageSize) || 10;
      
      // If we don't have an accurate total count and we're not on page 1, 
      // or if we got a full page of results, estimate that there might be more
      if (!response.data.data?.total && !response.data.data?.count) {
        if (currentPage === 1 && processedData.length === itemsPerPage) {
          // First page is full, likely there are more pages - estimate conservatively
          totalItems = processedData.length * 2; // Minimum estimate
        } else if (currentPage > 1) {
          // On subsequent pages, estimate based on current page
          totalItems = (currentPage - 1) * itemsPerPage + processedData.length;
        } else {
          totalItems = processedData.length;
        }
      } else {
        totalItems = response.data.data.total;
      }
      
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      
      return {
        status: response.data.status,
        data: {
          data: processedData,
          meta: {
            totalItems: totalItems,
            currentPage: currentPage,
            itemsPerPage: itemsPerPage,
            totalPages: totalPages
          }
        },
        message: response.data.message || ''
      };
    } else {
      // Fallback format
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching requisitions:', error);
    throw error;
  }
};

/**
 * Get assigned requisitions for a specific buyer
 * This allows buyers to view other buyers' assigned requisitions (e.g., in case of absence)
 * @param {string|number} userId - The user_id of the buyer whose requisitions to fetch
 * @returns {Promise} Response with the buyer's assigned requisitions
 */
export const getBuyerAssignedRequisitions = async (userId) => {
  try {
    // Build query params for assigned requisitions
    const queryParams = new URLSearchParams();
    queryParams.append('status', 'my_assigned');
    
    const url = `${REQUISITIONS_URL}?${queryParams}`;
    
    // Set up masquerade header to fetch requisitions as the specified buyer
    const masqueradeData = {
      user_id: userId,
      role: 6, // Buyer role
      department: 8 // Procurement department
    };
    
    const headers = {
      'X-Masquerade-User': JSON.stringify(masqueradeData)
    };
    
    console.log('getBuyerAssignedRequisitions - Fetching for user:', userId);
    console.log('Masquerade header:', JSON.stringify(masqueradeData));
    console.log('Full URL:', url);
    
    // Make API call masquerading as the specified buyer
    const response = await api.get(url, { headers });
    
    console.log('getBuyerAssignedRequisitions - Response received:', response.data);
    
    // Handle ApiRequisitions format: { status: 1, message: "Success", data: { rows: [...] } }
    if (response.data && response.data.status !== undefined) {
      const requisitionsData = response.data.data?.rows || [];
      
      console.log(`Found ${requisitionsData.length} requisitions for buyer ${userId}`);
      console.log('Sample requisition:', requisitionsData[0]);
      
      // Process data similar to getRequisitions
      const processedData = requisitionsData.map(req => {
        // Basic processing - full processing logic can be copied from getRequisitions if needed
        return { ...req };
      });
      
      // Get metadata
      const totalItems = response.data.data?.meta?.total || processedData.length;
      const currentPage = response.data.data?.meta?.page || 1;
      const itemsPerPage = response.data.data?.meta?.limit || processedData.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      
      return {
        status: response.data.status,
        data: {
          data: processedData,
          meta: {
            totalItems: totalItems,
            currentPage: currentPage,
            itemsPerPage: itemsPerPage,
            totalPages: totalPages
          }
        },
        message: response.data.message || ''
      };
    } else {
      // Fallback format
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching buyer assigned requisitions:', error);
    throw error;
  }
};

export const getRequisition = async (id) => {
  try {
    const response = await api.get(`${REQUISITIONS_URL}/${id}`);
    
    // Process the single requisition data similar to how we process the list
    if (response.data?.status === 'success' && response.data?.data) {
      const req = response.data.data;
      
      // Fix specification_items if it's serialized PHP data
      if (req.specification_items && typeof req.specification_items === 'string' && req.specification_items.startsWith('a:')) {
        try {
          // Parse PHP serialized array - this should return an array of items
          req.specification_items = parsePhpArray(req.specification_items) || [];
        } catch (e) {
          console.error('❌ Failed to parse specification_items:', e);
          req.specification_items = [];
        }
      } else if (!req.specification_items || !Array.isArray(req.specification_items)) {
        req.specification_items = [];
      }
      
      // Fix specification_qty if it's serialized PHP data
      if (req.specification_qty && typeof req.specification_qty === 'string' && req.specification_qty.startsWith('a:')) {
        try {
          req.specification_qty = parsePhpArray(req.specification_qty) || [];
        } catch (e) {
          console.error('❌ Failed to parse specification_qty:', e);
          req.specification_qty = [];
        }
      } else if (!req.specification_qty || !Array.isArray(req.specification_qty)) {
        req.specification_qty = [];
      }
      
      // Fix specification_amount if it's serialized PHP data
      if (req.specification_amount && typeof req.specification_amount === 'string' && req.specification_amount.startsWith('a:')) {
        try {
          req.specification_amount = parsePhpArray(req.specification_amount) || [];
        } catch (e) {
          console.error('❌ Failed to parse specification_amount:', e);
          req.specification_amount = [];
        }
      } else if (!req.specification_amount || !Array.isArray(req.specification_amount)) {
        req.specification_amount = [];
      }

        
      // Fix vat values if it's serialized PHP data
      if (req.specification_vats && typeof req.specification_vats === 'string' && req.specification_vats.startsWith('a:')) {
        try {
          req.specification_vats = parsePhpArray(req.specification_vats) || [];
        } catch (e) {
          console.error('❌ Failed to parse specification_vats:', e);
          req.specification_vats = [];
        }
      } else if (!req.specification_vats || !Array.isArray(req.specification_vats)) {
        req.specification_vats = [];
      }
      
      return req;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching requisition:', error);
    throw error;
  }
};

export const assignProcurement = async ({ requisitionId }) => {
  try {
    const formData = new FormData();
    formData.append('approval', requisitionId);
    
    const response = await api.post(`${REQUISITIONS_URL}/assign_procurement`, formData);
    return response.data;
  } catch (error) {
    console.error('Error assigning to procurement:', error);
    throw error;
  }
};

export const startRFQ = async ({ requisitionId }) => {
  try {
    const formData = new FormData();
    formData.append('requisition', requisitionId);
    
    const response = await api.post(`${REQUISITIONS_URL}/start_procurement`, formData);
    return response.data;
  } catch (error) {
    console.error('Error starting RFQ process:', error);
    throw error;
  }
};

export const selectQuotation = async ({ requisitionId }) => {
  try {
    // This function just redirects to the quotation selection page
    return { success: true };
  } catch (error) {
    console.error('Error with quotation selection:', error);
    throw error;
  }
};

export const finalApproval = async ({ requisitionId }) => {
  try {
    const formData = new FormData();
    formData.append('approval', requisitionId);
    
    const response = await api.post(`${REQUISITIONS_URL}/approval_fincon_pay`, formData);
    return response.data;
  } catch (error) {
    console.error('Error giving final approval:', error);
    throw error;
  }
};

export const createRequisition = async (requisitionData) => {
  try {
    // Create FormData to match backend expectations (PHP expects $_POST data)
    const formData = new FormData();
    
    // Map frontend fields to backend expected fields - use the actual field names sent
    formData.append('requisition_motivation', requisitionData.requisition_motivation || '');
    formData.append('requisition_influence', requisitionData.requisition_influence || '');
    formData.append('requisition_declaration', requisitionData.requisition_declaration || '');
    formData.append('requisition_spec_id', requisitionData.requisition_spec_id || '');
    formData.append('requisition_quote', requisitionData.requisition_quote || '');
    formData.append('requisition_title', requisitionData.requisition_title || '');
    formData.append('requisition_user', requisitionData.requisition_user || '');
    
    // Handle file uploads
    if (requisitionData.files && Array.isArray(requisitionData.files)) {
      requisitionData.files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });
    }
    
    // Use the actual API call
    const response = await api.post(REQUISITIONS_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating requisition:', error);
    throw error;
  }
};

export const updateRequisition = async (id, requisitionData) => {
  try {
    // Use FormData like other functions to match backend expectations
    const formData = new FormData();
    formData.append('requisition_id', id);
    
    // Add all the requisition data fields
    Object.keys(requisitionData).forEach(key => {
      if (requisitionData[key] !== null && requisitionData[key] !== undefined) {
        formData.append(key, requisitionData[key]);
      }
    });
    
    const response = await api.put(`${REQUISITIONS_URL}/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating requisition:', error);
    throw error;
  }
};

export const updateRequisitionDeclaration = async (requisitionId, declarationData) => {
  try {
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    formData.append('requisition_influence', declarationData.requisition_influence || '0');
    formData.append('requisition_declaration', declarationData.requisition_declaration || '');
    
    // Use the same endpoint as update but with PUT method like the original updateRequisition
    const response = await api.put(`${REQUISITIONS_URL}/${requisitionId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating requisition declaration:', error);
    throw error;
  }
};

export const saveDeclarationViaComment = async (requisitionId, declarationData, requisitionNumber, userId) => {
  try {
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    formData.append('requisition_number', requisitionNumber || '');
    formData.append('requisition_user', userId || '');
    formData.append('requisition_comment', 'Declaration updated');
    formData.append('requisition_influence', declarationData.requisition_influence || '0');
    formData.append('requisition_declaration', declarationData.requisition_declaration || '');
    
    const response = await api.post(`${REQUISITIONS_URL}/comment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving declaration via comment:', error);
    throw error;
  }
};

export const deleteRequisition = async (id) => {
  try {
    const response = await api.delete(`${REQUISITIONS_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting requisition:', error);
    throw error;
  }
};

export const getRequisitionSuppliers = async (id) => {
  try {
    const response = await api.get(`${REQUISITIONS_URL}/${id}/suppliers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching requisition suppliers:', error);
    throw error;
  }
};

export const updateRequisitionStatus = async (id, status, notes = '') => {
  try {
    const response = await api.patch(`${REQUISITIONS_URL}/${id}/status`, {
      status,
      notes
    });
    return response.data;
  } catch (error) {
    console.error('Error updating requisition status:', error);
    throw error;
  }
};

export const approveRequisition = async (id, notes = '') => {
  try {
    const response = await api.post(`${REQUISITIONS_URL}/${id}/approve`, {
      notes
    });
    return response.data;
  } catch (error) {
    console.error('Error approving requisition:', error);
    throw error;
  }
};

export const rejectRequisition = async (id, notes = '') => {
  try {
    const response = await api.post(`${REQUISITIONS_URL}/${id}/reject`, {
      notes
    });
    return response.data;
  } catch (error) {
    console.error('Error rejecting requisition:', error);
    throw error;
  }
};

export const getBudgetItems = async () => {
  try {
    const response = await api.get('/api/budget-items');
    return response.data;
  } catch (error) {
    console.error('Error fetching budget items:', error);
    throw error;
  }
};

export const getRequisitionStatistics = async () => {
  try {
    const response = await api.get(`${REQUISITIONS_URL}/statistics`);
    return response.data;
  } catch (error) {
    console.error('Error fetching requisition statistics:', error);
    throw error;
  }
};

export const getRequisitionDetails = async (id) => {
  try {
    const url = `${REQUISITIONS_URL}/show/${id}`;
    
    // Prepare headers with user context for masquerading support
    const headers = {};
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    if (authContext.user && authContext.isMasquerading) {
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    const response = await api.get(url, { headers });
    
    // Check if the response has the expected structure
    if (!response.data || typeof response.data !== 'object') {
      console.error('❌ Invalid response structure:', response.data);
      return { error: 'Invalid response from server' };
    }
    
    // Check if this is an error response with a message
    if (response.data.error || (response.data.status === 0 && response.data.message)) {
      console.error('❌ API returned error:', response.data.error || response.data.message);
      return { error: response.data.error || response.data.message };
    }
    
    // Extract the actual data object if it exists in a nested structure
    let requisitionData = response.data;
    if (response.data.status === 1 && response.data.data) {
      requisitionData = response.data.data;
    }
    
    // Parse PHP serialized arrays if needed
    if (requisitionData.specification_items && typeof requisitionData.specification_items === 'string' && 
        requisitionData.specification_items.startsWith('a:')) {
      try {
        // Basic parsing for PHP serialized arrays in the format a:1:{i:0;s:3:"ddd";}
        const matches = requisitionData.specification_items.match(/s:\d+:"([^"]*)"/g) || [];
        requisitionData.specification_items = matches.map(m => m.replace(/s:\d+:"([^"]*)"/, '$1'));
      } catch (e) {
        console.warn('❓ Could not parse specification_items:', e);
        requisitionData.specification_items = [];
      }
    }
    
    // Do the same for specification_qty
    if (requisitionData.specification_qty && typeof requisitionData.specification_qty === 'string' && 
        requisitionData.specification_qty.startsWith('a:')) {
      try {
        const matches = requisitionData.specification_qty.match(/s:\d+:"([^"]*)"/g) || [];
        requisitionData.specification_qty = matches.map(m => m.replace(/s:\d+:"([^"]*)"/, '$1'));
      } catch (e) {
        console.warn('❓ Could not parse specification_qty:', e);
        requisitionData.specification_qty = [];
      }
    }
    
    // Ensure comments and submissions are arrays
    if (!requisitionData.comments) requisitionData.comments = [];
    if (!requisitionData.submissions) requisitionData.submissions = [];
    
    return requisitionData;
  } catch (error) {
    console.error('❌ Error fetching requisition details:', error);
    console.error('❌ Error response:', error.response);
    console.error('❌ Error message:', error.message);
    return { error: error.message || 'Failed to fetch requisition details' };
  }
};

export const addComment = async (requisitionId, commentData) => {
  try {
    console.log('🔍 addComment API payload:', {
      requisitionId,
      commentData,
      requisition_user: commentData.requisition_user,
      is_masquerading: commentData.is_masquerading,
      masqueraded_by: commentData.masqueraded_by,
      on_behalf_of: commentData.on_behalf_of,
      comment_approved: commentData.comment_approved
    });
    
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    formData.append('requisition_comment', commentData.comment);
    formData.append('requisition_number', commentData.requisition_number || '');
    formData.append('requisition_user', commentData.requisition_user || '');
    
    // Add the commenter name (equivalent to original form's requisition_comment_name)
    formData.append('requisition_comment_name', commentData.commenter_name || '');
    
    // Add the comment_approved flag if present (for approval comments)
    if (commentData.comment_approved) {
      formData.append('comment_approved', '1');
    }
    
    // Add auto_approve_next flag if present (for automatic approval of next level when user is both approvers)
    if (commentData.auto_approve_next !== undefined) {
      formData.append('auto_approve_next', commentData.auto_approve_next ? '1' : '0');
      console.log('🔍 Auto-approve next setting added to FormData:', commentData.auto_approve_next);
    }
    
    // Add masquerading context if present
    if (commentData.is_masquerading) {
      formData.append('is_masquerading', '1');
      formData.append('original_user_id', commentData.original_user_id || '');
      formData.append('masqueraded_by', commentData.masqueraded_by || '');
      formData.append('on_behalf_of', commentData.on_behalf_of || '');
      formData.append('masquerading_user_id', commentData.on_behalf_of || ''); // The user being masqueraded as
    }
    
    if (commentData.reject) {
      formData.append('reject', '1');
    }
    
    if (commentData.additionalInfo) {
      formData.append('additional_info', '1');
    }
    
    if (commentData.second_approval) {
      formData.append('second_approval', commentData.second_approval);
    }
    
    // Debug: Log all FormData entries
    console.log('🔍 FormData entries being sent:');
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}: ${value}`);
    }
    
    const response = await api.post(`${REQUISITIONS_URL}/comment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

export const assignRequisition = async (requisitionId, assignData) => {
  try {
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    formData.append('requisition_assign_id', assignData.assignToUserId);
    formData.append('specification_title', assignData.specification_title || '');
    
    const response = await api.post(`${REQUISITIONS_URL}/assign`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning requisition:', error);
    throw error;
  }
};

export const amendRequisition = async (requisitionId, amendData) => {
  try {
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    formData.append('ref', amendData.ref || '');
    
    const response = await api.post(`${REQUISITIONS_URL}/amend`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating amendment:', error);
    throw error;
  }
};

export const updateCapexNumber = async (requisitionId, capexData) => {
  try {
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    formData.append('capex_number', capexData.capexNumber);
    formData.append('ref', capexData.ref || '');
    
    const response = await api.post(`${REQUISITIONS_URL}/capex_number`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating capex number:', error);
    throw error;
  }
};

export const requestPMApproval = async (requisitionId) => {
  try {
    console.log(`Requesting PM approval for requisition ID: ${requisitionId}`);
    const response = await api.post(`/requisitions/${requisitionId}/pm_approval`, {
      requisition_id: requisitionId
    });
    console.log('PM approval response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in PM approval request:', error.response?.data || error.message);
    throw error;
  }
};

export const requestReviewResend = async (requisitionId, comment = '') => {
  try {
    console.log(`Resending approval to current approver for requisition ID: ${requisitionId}`);
    console.log('requestReviewResend - Comment parameter:', comment);
    console.log('requestReviewResend - Comment type:', typeof comment);
    console.log('requestReviewResend - Comment length:', comment?.length);
    
    const response = await api.post(`/requisitions/${requisitionId}/request_review`, {
      requisition_id: requisitionId,
      comment: comment
    });
    console.log('Request review response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error in Request Review:', error.response?.data || error.message);
    throw error;
  }
};

export const requestSapApproval = async (requisitionId, sapData = {}) => {
  try {
    console.log('🔍 requestSapApproval starting with requisitionId:', requisitionId);
    
    const formData = new FormData();
    formData.append('requisition_id', requisitionId);
    
    // Add SAP draft number if provided
    if (sapData.sap_draft_number) {
      formData.append('sap_draft_number', sapData.sap_draft_number);
    }
    
    // Add copy me flag if provided
    if (sapData.CC) {
      formData.append('CC', sapData.CC);
    }
    
    // Prepare headers and FormData with masquerading context
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    console.log('🔍 requestSapApproval authContext:', authContext);
    
    if (authContext.user && authContext.isMasquerading) {
      console.log('🎭 Adding masquerading context to SAP approval request');
      
      // Add masquerading context to FormData (for PHP $_POST access)
      formData.append('is_masquerading', '1');
      formData.append('original_user_id', authContext.originalUser?.user_id || '');
      formData.append('masqueraded_by', authContext.originalUser?.user_id || '');
      formData.append('on_behalf_of', authContext.user.user_id || '');
      formData.append('masquerading_user_id', authContext.user.user_id || '');
      
      console.log('🔍 Masquerading FormData:', {
        is_masquerading: '1',
        original_user_id: authContext.originalUser?.user_id,
        masqueraded_by: authContext.originalUser?.user_id,
        on_behalf_of: authContext.user.user_id,
        masquerading_user_id: authContext.user.user_id
      });
      
      // Also add to headers (for consistent approach)
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
      
      console.log('🔍 Masquerading Header:', headers['X-Masquerade-User']);
    }
    
    console.log('🚀 Making SAP approval request to backend...');
    const response = await api.post(`${REQUISITIONS_URL}/sap_approval`, formData, { headers });
    console.log('✅ SAP approval response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error requesting SAP approval:', error);
    console.error('❌ Error response:', error.response?.data);
    throw error;
  }
};

export const sendToJunk = async (requisitionId, junkReason, masqueradingContext = null) => {
  try {
    const formData = new FormData();
    formData.append('junk_reason', junkReason);

    // Add masquerading context if present
    if (masqueradingContext && masqueradingContext.is_masquerading) {
      formData.append('is_masquerading', '1');
      formData.append('original_user_id', masqueradingContext.original_user_id || '');
      formData.append('masqueraded_by', masqueradingContext.masqueraded_by || '');
      formData.append('on_behalf_of', masqueradingContext.on_behalf_of || '');
      formData.append('masquerading_user_id', masqueradingContext.on_behalf_of || '');
    }

    // Use the API controller endpoint instead of direct PHP controller
    const response = await api.post(`${REQUISITIONS_URL}/to_junk/${requisitionId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error sending to junk:', error);
    throw error;
  }
};

export const uploadPurchaseOrder = async (formData) => {
  try {
    console.log('🔍 uploadPurchaseOrder starting with formData');
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    console.log('🔍 uploadPurchaseOrder authContext:', authContext);
    
    // Prepare headers for file upload
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    
    if (authContext.user && authContext.isMasquerading) {
      console.log('🎭 Adding masquerading context to Purchase Order upload');
      
      // Add masquerading context to FormData (for PHP $_POST access)
      formData.append('is_masquerading', '1');
      formData.append('original_user_id', authContext.originalUser?.user_id || '');
      formData.append('masqueraded_by', authContext.originalUser?.user_id || '');
      formData.append('on_behalf_of', authContext.user.user_id || '');
      formData.append('masquerading_user_id', authContext.user.user_id || '');
      
      console.log('🔍 Masquerading FormData for PO upload:', {
        is_masquerading: '1',
        original_user_id: authContext.originalUser?.user_id,
        masqueraded_by: authContext.originalUser?.user_id,
        on_behalf_of: authContext.user.user_id,
        masquerading_user_id: authContext.user.user_id
      });
      
      // Also add to headers (for consistent approach)
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
      
      console.log('🔍 Masquerading Header for PO:', headers['X-Masquerade-User']);
    }
    
    console.log('🚀 Making Purchase Order upload request to backend...');
    const response = await api.post(`${REQUISITIONS_URL}/upload_po`, formData, { headers });
    console.log('✅ Purchase Order upload response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error uploading Purchase Order:', error);
    console.error('❌ Error response:', error.response?.data);
    throw error;
  }
};

export const getArchiveRequisitions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('limit', params.pageSize);
    if (params.filter) queryParams.append('search', params.filter);

    const url = queryParams.toString() ? `${REQUISITIONS_URL}/archive?${queryParams}` : `${REQUISITIONS_URL}/archive`;
    
    // Prepare headers with user context for masquerading support
    const headers = {};
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    if (authContext.user && authContext.isMasquerading) {
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    const response = await api.get(url, { headers });
    
    // Handle ApiRequisitions format
    if (response.data && response.data.status !== undefined) {
      const requisitionsData = response.data.data?.rows || [];
      
      // Process data similar to getRequisitions
      const processedData = requisitionsData.map((req) => {
        const newReq = { ...req };
        
        // Handle missing user data
        if (!newReq.user_full_name || newReq.user_full_name === 'Unknown' || newReq.user_full_name === '') {
          const userFields = ['requester_name', 'user_name', 'author_name'];
          for (const field of userFields) {
            if (newReq[field] && newReq[field] !== 'Unknown' && newReq[field] !== '') {
              newReq.user_full_name = newReq[field];
              break;
            }
          }
          if (!newReq.user_full_name) {
            newReq.user_full_name = 'System User';
          }
        }
        
        return newReq;
      });
      
      const currentPage = parseInt(params.page) || 1;
      const itemsPerPage = parseInt(params.pageSize) || 20;
      let totalItems = response.data.data?.total || processedData.length;
      
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      
      return {
        status: response.data.status,
        data: {
          data: processedData,
          meta: {
            totalItems: totalItems,
            currentPage: currentPage,
            itemsPerPage: itemsPerPage,
            totalPages: totalPages
          }
        },
        message: response.data.message || ''
      };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching archived requisitions:', error);
    throw error;
  }
};

export const getJunkRequisitions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('limit', params.pageSize);
    if (params.filter) queryParams.append('search', params.filter);

    const url = queryParams.toString() ? `${REQUISITIONS_URL}/junk?${queryParams}` : `${REQUISITIONS_URL}/junk`;
    
    // Prepare headers with user context for masquerading support
    const headers = {};
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    if (authContext.user && authContext.isMasquerading) {
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    const response = await api.get(url, { headers });
    
    // Handle ApiRequisitions format
    if (response.data && response.data.status !== undefined) {
      const requisitionsData = response.data.data?.rows || [];
      
      // Process data similar to getRequisitions
      const processedData = requisitionsData.map((req) => {
        const newReq = { ...req };
        
        // Handle missing user data
        if (!newReq.user_full_name || newReq.user_full_name === 'Unknown' || newReq.user_full_name === '') {
          const userFields = ['requester_name', 'user_name', 'author_name'];
          for (const field of userFields) {
            if (newReq[field] && newReq[field] !== 'Unknown' && newReq[field] !== '') {
              newReq.user_full_name = newReq[field];
              break;
            }
          }
          if (!newReq.user_full_name) {
            newReq.user_full_name = 'System User';
          }
        }
        
        return newReq;
      });
      
      const currentPage = parseInt(params.page) || 1;
      const itemsPerPage = parseInt(params.pageSize) || 20;
      let totalItems = response.data.data?.total || processedData.length;
      
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      
      return {
        status: response.data.status,
        data: {
          data: processedData,
          meta: {
            totalItems: totalItems,
            currentPage: currentPage,
            itemsPerPage: itemsPerPage,
            totalPages: totalPages
          }
        },
        message: response.data.message || ''
      };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching junk requisitions:', error);
    throw error;
  }
};

// Purchase Order specific functions
export const getPurchaseOrders = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('limit', params.pageSize);
    if (params.filter) queryParams.append('search', params.filter);
    
    // Add parameter to get PO-related requisitions
    if (params.status && params.status === 'ready_for_po') {
      queryParams.append('po_ready', 'true');
    } else {
      queryParams.append('po_only', 'true');
    }
    
    const url = queryParams.toString() ? `${REQUISITIONS_URL}?${queryParams}` : `${REQUISITIONS_URL}?po_only=true`;
    
    // Prepare headers with user context for masquerading support
    const headers = {};
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    if (authContext.user && authContext.isMasquerading) {
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    const response = await api.get(url, { headers });
    
    if (response.data && response.data.status !== undefined) {
      // Don't filter out rows - return all requisitions that match the criteria
      const purchaseOrders = response.data.data?.rows || [];
      
      console.log('PurchaseOrders API response:', response.data);
      console.log('Found purchase orders:', purchaseOrders.length);
      
      // Return in the same format as getRequisitions for consistency
      return {
        status: 1,
        message: `Found ${purchaseOrders.length} purchase orders`,
        data: {
          data: purchaseOrders,
          meta: response.data.data?.meta || {},
          rows: purchaseOrders,
          total: purchaseOrders.length
        }
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    return {
      status: 0,
      message: error.message || 'Failed to fetch purchase orders',
      data: {
        data: [],
        rows: [],
        total: 0
      }
    };
  }
};

export const updatePurchaseOrder = async (formData) => {
  try {
    console.log('🔍 updatePurchaseOrder starting with formData:', formData);
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    console.log('🔍 updatePurchaseOrder authContext:', authContext);
    
    // Prepare data for the existing update_po endpoint
    const updateData = {
      requisition_id: formData.requisition_id,
      quotation_items: formData.quotation_items,
      quotation_qty: formData.quotation_qty,
      quotation_units: formData.quotation_units,
      quotation_discounts: formData.quotation_discounts,
      quotation_amounts: formData.quotation_amounts,
      quotation_vats: formData.quotation_vats.join(','), // Convert array to comma-separated string
      quotation_before_discount: formData.quotation_before_discount,
      quotation_discount: formData.quotation_discount,
      quotation_subtotal: formData.quotation_subtotal,
      quotation_vat: formData.quotation_vat,
      quotation_total: formData.quotation_total,
      discount: formData.discount_type,
      vat: formData.vat_type,
      complete: true // This indicates it's a PO submission
    };
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authContext.user && authContext.isMasquerading) {
      console.log('🎭 Adding masquerading context to PO update');
      
      // Add masquerading context to request data
      updateData.is_masquerading = true;
      updateData.original_user_id = authContext.originalUser?.user_id || '';
      updateData.masqueraded_by = authContext.originalUser?.user_id || '';
      updateData.on_behalf_of = authContext.user.user_id || '';
      updateData.masquerading_user_id = authContext.user.user_id || '';
      
      // Also add to headers
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    // Use existing upload_po endpoint which handles PO updates
    const response = await api.post(`${REQUISITIONS_URL}/upload_po`, updateData, { headers });
    
    console.log('✅ PO update response:', response.data);
    
    if (response.data && (response.data.success || response.data.status === 1)) {
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || 'Purchase order updated successfully'
      };
    } else {
      throw new Error(response.data?.message || 'Failed to update purchase order');
    }
    
  } catch (error) {
    console.error('❌ updatePurchaseOrder error:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to update purchase order');
  }
};

export const deletePurchaseOrder = async (requisitionId) => {
  try {
    console.log('🔍 deletePurchaseOrder starting with requisitionId:', requisitionId);
    
    const response = await api.delete(`${REQUISITIONS_URL}/${requisitionId}`);
    
    if (response.data && response.data.success) {
      return {
        success: true,
        message: 'Purchase order deleted successfully'
      };
    } else {
      throw new Error(response.data?.message || 'Failed to delete purchase order');
    }
    
  } catch (error) {
    console.error('❌ deletePurchaseOrder error:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to delete purchase order');
  }
};

export const getPurchaseOrderDetails = async (requisitionId) => {
  try {
    console.log('🔍 getPurchaseOrderDetails starting with requisitionId:', requisitionId);
    
    // Use existing getRequisitionDetails function since PO data is part of requisition
    const response = await getRequisitionDetails(requisitionId);
    
    if (response.success || response.data) {
      const requisitionData = response.data || response;
      
      // Verify this requisition has PO data
      if (!requisitionData.requisition_po_date || requisitionData.requisition_po_date <= 0) {
        throw new Error('This requisition has not been converted to a purchase order');
      }
      
      return {
        success: true,
        data: requisitionData,
        message: 'Purchase order details loaded successfully'
      };
    }
    
    throw new Error(response.error || 'Failed to load purchase order details');
    
  } catch (error) {
    console.error('❌ getPurchaseOrderDetails error:', error);
    return {
      success: false,
      data: null,
      message: error.message || 'Failed to load purchase order details'
    };
  }
};

export const downloadPurchaseOrder = async (requisitionId, filename) => {
  try {
    console.log('🔍 downloadPurchaseOrder starting:', { requisitionId, filename });
    
    // Get requisition data to find file path
    const response = await getRequisitionDetails(requisitionId);
    
    if (response.success || response.data) {
      const data = response.data || response;
      
      if (data.requisition_po && data.quotation_directory) {
        const downloadUrl = `/files/download?dl=${data.quotation_directory}/${data.requisition_po}`;
        
        // Open in new window for download
        window.open(downloadUrl, '_blank');
        
        return {
          success: true,
          message: 'Purchase order download initiated'
        };
      } else {
        throw new Error('Purchase order file not found');
      }
    }
    
    throw new Error('Failed to get purchase order information');
    
  } catch (error) {
    console.error('❌ downloadPurchaseOrder error:', error);
    return {
      success: false,
      message: error.message || 'Failed to download purchase order'
    };
  }
};

export const getPurchaseOrderStatistics = async () => {
  try {
    console.log('🔍 getPurchaseOrderStatistics starting');
    
    // Get all requisitions and filter for POs
    const response = await getRequisitions({ po_only: true });
    
    if (response.success || response.data) {
      const pos = response.data?.data || response.data || [];
      
      const stats = {
        totalPOs: pos.length,
        totalValue: pos.reduce((sum, po) => sum + (parseFloat(po.requisition_total) || 0), 0),
        issuedPOs: pos.filter(po => po.requisition_po_date && po.requisition_po_date > 0).length,
        pendingPOs: pos.filter(po => !po.requisition_po_date || po.requisition_po_date <= 0).length,
        averageValue: pos.length > 0 ? pos.reduce((sum, po) => sum + (parseFloat(po.requisition_total) || 0), 0) / pos.length : 0
      };
      
      return {
        success: true,
        data: stats,
        message: 'Purchase order statistics loaded successfully'
      };
    }
    
    throw new Error('Failed to load requisitions for statistics');
    
  } catch (error) {
    console.error('❌ getPurchaseOrderStatistics error:', error);
    return {
      success: false,
      data: null,
      message: error.message || 'Failed to load purchase order statistics'
    };
  }
};

/**
 * Get all requisitions for reporting without pagination limits
 * @param {Object} params - Optional search parameters
 * @returns {Promise} API response with all requisitions
 */
export const getRequisitionsReports = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.emergency !== undefined && params.emergency !== '') queryParams.append('emergency', params.emergency);
    
    const url = queryParams.toString() ? `/api/requisitions/reports?${queryParams}` : '/api/requisitions/reports';
    
    // Prepare headers with user context for masquerading support
    const headers = {};
    
    // Get current user from auth context (may be masqueraded user)
    const authContext = window.authContext || JSON.parse(localStorage.getItem('authContext') || '{}');
    if (authContext.user && authContext.isMasquerading) {
      headers['X-Masquerade-User'] = JSON.stringify({
        user_id: authContext.user.user_id,
        department: authContext.user.department,
        role: authContext.user.role
      });
    }
    
    // Make API call with user context in headers
    const response = await api.get(url, { headers });
    
    // Handle the response format: { status: 1, message: "Success", data: { rows: [...], total: number } }
    if (response.data && response.data.status === 1) {
      return response.data;
    } else {
      throw new Error(response.data?.message || 'Failed to fetch requisitions reports');
    }
    
  } catch (error) {
    console.error('Error fetching requisitions reports:', error);
    throw error;
  }
};
